import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Computer as ComputerIcon,
  SettingsAccessibility as SettingsAccessibilityIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  DesktopWindows as DesktopWindowsIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Keyboard as KeyboardIcon
} from '@mui/icons-material';
import PageHeader from '../components/shared/page-header';

interface ScreenReader {
  name: string;
  description: string;
  icon: React.ReactElement;
  link: string;
  platform: string;
}

const AccessibilityComponent: React.FC = () => {
  const screenReaders: ScreenReader[] = [
    {
      name: 'NVDA (NonVisual Desktop Access)',
      description: 'Leitor de tela gratuito e de código aberto para Windows. Uma das ferramentas mais populares e completas disponíveis.',
      icon: <ComputerIcon sx={{ fontSize: 48, color: 'brand.text' }} />,
      link: 'https://www.nvaccess.org/download/',
      platform: 'Windows'
    },
    {
      name: 'JAWS (Job Access With Speech)',
      description: 'Leitor de tela profissional para Windows, amplamente utilizado em ambientes corporativos e educacionais.',
      icon: <SettingsAccessibilityIcon sx={{ fontSize: 48, color: 'brand.text' }} />,
      link: 'https://www.freedomscientific.com/products/software/jaws/',
      platform: 'Windows'
    },
    {
      name: 'VoiceOver',
      description: 'Leitor de tela nativo da Apple, integrado ao macOS, iOS e iPadOS. Não requer instalação adicional.',
      icon: <AppleIcon sx={{ fontSize: 48, color: 'brand.text' }} />,
      link: 'https://www.apple.com/br/accessibility/voiceover/',
      platform: 'macOS / iOS'
    },
    {
      name: 'TalkBack',
      description: 'Leitor de tela do Google para dispositivos Android. Vem pré-instalado na maioria dos aparelhos.',
      icon: <AndroidIcon sx={{ fontSize: 48, color: 'brand.text' }} />,
      link: 'https://support.google.com/accessibility/android/answer/6283677',
      platform: 'Android'
    },
    {
      name: 'Orca',
      description: 'Leitor de tela gratuito e de código aberto para sistemas Linux, integrado ao ambiente GNOME.',
      icon: <DesktopWindowsIcon sx={{ fontSize: 48, color: 'brand.text' }} />,
      link: 'https://help.gnome.org/users/orca/stable/',
      platform: 'Linux'
    }
  ];

  const accessibilityFeatures = [
    'Alto contraste para melhor visualização',
    'Navegação completa por teclado',
    'Textos alternativos em todas as imagens',
    'Estrutura semântica e hierárquica',
    'Legendas e transcrições quando aplicável',
    'Fonte ajustável para melhor leitura'
  ];

  const keyboardShortcuts = [
    { key: 'Tab', action: 'Avançar entre elementos interativos' },
    { key: 'Shift + Tab', action: 'Voltar entre elementos interativos' },
    { key: 'Enter', action: 'Ativar links e botões' },
    { key: 'Espaço', action: 'Ativar botões e checkboxes' },
    { key: 'Setas', action: 'Navegar por menus e listas' },
    { key: 'Esc', action: 'Fechar modais e menus' }
  ];

  return (
    <Box>
      <PageHeader
        title="Acessibilidade"
        subtitle="Compromisso com a inclusão digital e acessibilidade para todos os cidadãos."
        breadcrumbs={[{ label: 'Acessibilidade' }]}
      />

      {/* Introduction Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'brand.text' }}>
            Nosso Compromisso com a Acessibilidade
          </Typography>
          <Typography variant="body1" paragraph>
            Este portal foi desenvolvido seguindo as diretrizes de acessibilidade web (WCAG - Web Content
            Accessibility Guidelines), com o objetivo de garantir que todos os cidadãos, independentemente
            de suas capacidades físicas, sensoriais ou cognitivas, possam acessar as informações e
            serviços públicos disponibilizados.
          </Typography>
          <Typography variant="body1" paragraph>
            A inclusão digital é um direito fundamental e uma prioridade da gestão municipal. Estamos
            comprometidos em fornecer uma experiência de navegação acessível, compreensível e utilizável
            para todos os usuários, incluindo pessoas com deficiência visual, auditiva, motora ou cognitiva.
          </Typography>
          <Typography variant="body1">
            Esta página fornece informações sobre os recursos de acessibilidade implementados no site,
            ferramentas recomendadas e orientações para facilitar sua navegação.
          </Typography>
        </Paper>

        {/* Accessibility Features Section */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'brand.text' }}>
            Recursos de Acessibilidade do Site
          </Typography>
          <Typography variant="body1" paragraph>
            Implementamos diversos recursos para tornar o portal mais acessível:
          </Typography>
          <List>
            {accessibilityFeatures.map((feature, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText primary={feature} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Screen Readers Section */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'brand.text' }}>
            Leitores de Tela Recomendados
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            Ferramentas gratuitas para navegação assistida
          </Typography>

          <Grid container spacing={3}>
            {screenReaders.map((reader, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {reader.icon}
                      <Box sx={{ ml: 2, flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {reader.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Plataforma: {reader.platform}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ flexGrow: 1 }}>
                      {reader.description}
                    </Typography>
                    <Button
                      variant="contained"
                      endIcon={<OpenInNewIcon />}
                      href={reader.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      fullWidth
                    >
                      Acessar Site Oficial
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Keyboard Navigation Section */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <KeyboardIcon sx={{ fontSize: 32, color: 'brand.text', mr: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'brand.text' }}>
              Navegação por Teclado
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Todo o conteúdo do portal pode ser acessado utilizando apenas o teclado. Confira os
            atalhos principais:
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>
                    Tecla/Combinação
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>
                    Ação
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keyboardShortcuts.map((shortcut, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'monospace',
                          bgcolor: 'grey.200',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.9rem',
                          fontWeight: 600
                        }}
                      >
                        {shortcut.key}
                      </Typography>
                    </TableCell>
                    <TableCell>{shortcut.action}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Contact Section */}
        <Alert
          severity="info"
          sx={{
            mb: 3,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Encontrou alguma barreira de acessibilidade?
          </Typography>
          <Typography variant="body2" paragraph>
            Estamos constantemente trabalhando para melhorar a acessibilidade do nosso portal.
            Se você encontrar qualquer dificuldade ao navegar ou acessar algum conteúdo, por favor,
            entre em contato conosco.
          </Typography>
          <Typography variant="body2">
            Sua opinião é fundamental para tornarmos este portal cada vez mais acessível a todos.
          </Typography>
        </Alert>
    </Box>
  );
};

export default AccessibilityComponent;
